import {
  File,
  InitialFieldsWithFileName,
  Signatory,
  Signature,
} from "@/type/core/dashboard/dashboard.types";
import { ModelTransaction } from "@/type/core/models/models.types";
import { DEFAULT_SIGNATURE_REQUIREMENT_MODE } from "@/utils/Constant";
import { SignaturePatternEnum } from "@/utils/enums/signature/signature.pattern.enum";
import DateHelper from "@/utils/helpers/date-helper";
import { FileHelper } from "@/utils/helpers/file-helper";
import SignatureHelper from "@/utils/helpers/signature-helper";
import { TransactionHelper } from "@/utils/helpers/transaction-helper";
import {
  requestCreateModelTransaction,
  requestUpdateModelTransaction,
} from "@state/transactionModels/TransactionModelEffects";
import {
  addTransactionSignature,
  afterTransactionFieldUpdated,
  applyFirstInitialFieldsToOthers,
  applyInitialFieldsToFile,
  ChangePictureName,
  changeSignatureFromFirstRank,
  changeTransactionSignatureName,
  changeTransactionSignaturePicturePriorityForAll,
  DeleteSignature,
  deleteTransactionSignature,
  moveModelOrScellementTransactionSignature,
  MoveSignature,
  moveTransactionSignature,
  PicturePriority,
  removeInitialFieldsFromFileName,
  removeTransactionAttachment,
  removeTransactionFile,
  RemoveTransactionFilePayload,
  ResetPicture,
  resetTransaction,
  resetTransactionSignature,
  setCurrentTransactionDetail,
  setCurrentTransactionField,
  setCurrentTransactionFromModel,
  setSignatoryLevel,
  setSignatoryPattern,
  setSignatoryPhoneIsUserJSE,
  setSignatoryWithCertificate,
  setSignatureRequirementMode,
  setTransactionAttachments,
  setTransactionFiles,
  setTransactionIsFromDraft,
  setTransactionSignatories,
  setTransactionSignatures,
  setTransactionType,
  SignatoryLevel,
  SignatoryPattern,
  SignatoryWithCertificate,
  updateFileWithPageNumber,
} from "@state/transactions/CurrentTransactionEvents";
import { requestCreateTransaction } from "@state/transactions/TransactionEffects";
import {
  CurrentTransactionHandler,
  SignatureRequirementMode,
  TransactionDetail,
  TransactionType,
  TransactionUpdateField,
} from "@type/core/transaction/transaction.types";
import { SignaturePicturePriorityEnum } from "@utils/enums/signature.picture.priority.enum";
import { createStore, sample } from "effector";

const initialState: CurrentTransactionHandler = {
  transactionForm: {},
  transactionType: "signature",
  attachments: [],
  files: [],
  signatories: [],
  signatures: [],
  transactionDetail: undefined,
  isFromDraft: false,
  signatureRequirementMode: DEFAULT_SIGNATURE_REQUIREMENT_MODE,
};

const emptySignature: Signature = {
  positionX: 0,
  positionY: 0,
  page: 0,
  isMoved: false,
};

export const buildEmptySignature = (
  signature: Partial<Signature> = emptySignature,
): Signature => {
  return signature;
};

export const buildEmptyScellement = (
  scellementPosition: Pick<
    Signature,
    | "positionX"
    | "positionY"
    | "page"
    | "isMoved"
    | "signaturePriorityRule"
    | "identificator"
    | "signaturePictureRank"
  > = emptySignature,
): Signature => ({
  email: "scellement",
  pattern: SignaturePatternEnum.SEAL,
  page: scellementPosition?.page ?? 0,
  rank: 1,
  positionX: scellementPosition?.positionX ?? 0,
  positionY: scellementPosition?.positionY ?? 0,
  isMoved: scellementPosition?.isMoved ?? false,
  signaturePriorityRule:
    scellementPosition?.signaturePriorityRule ??
    SignaturePicturePriorityEnum.NO_PICTURE,
  identificator: scellementPosition.identificator,
  signaturePictureRank: scellementPosition?.signaturePictureRank ?? 0,
});

function updateSignatures(
  existingSignatures: Signature[],
  newSignature: Signature,
): Signature[] {
  const updatedSignatures = [...existingSignatures]; // Create a copy of the original array

  const index = updatedSignatures.findIndex((signature) => {
    return signature.identificator === newSignature.identificator;
  });
  // if email === null it means it come from the add a signature button from the model
  if (index !== -1 && newSignature.email != null) {
    // Replace existing user
    updatedSignatures[index] = newSignature;
  } else {
    // Add new user
    updatedSignatures.push(newSignature);
  }

  return updatedSignatures;
}

function applySignatureFromFirstRank(
  existingSignatures: Signature[],
  files: File[],
): Signature[] {
  const orderOneSignatures = existingSignatures.filter((sig) => sig.rank === 1);
  orderOneSignatures.sort(
    (a, b) => a.email?.localeCompare(b.email as string) as number,
  );

  const restOfTheSignatures: Signature[] = [];

  // we duplicate each signature of the first file to each files
  for (let fileNumber = 1; fileNumber < files.length; fileNumber++) {
    let signatureCounter = 0;
    let lastSignatoryIndex = -1;

    for (const orderOneSignature of orderOneSignatures) {
      const isPageValidForThisDocument =
        orderOneSignature &&
        orderOneSignature.page &&
        orderOneSignature.page <= (files[fileNumber].pages ?? -1);
      const isEmptySignature = orderOneSignature && !orderOneSignature.isMoved;

      if (!isPageValidForThisDocument && !isEmptySignature) continue;

      const currentSignatoryIndex =
        SignatureHelper.getSignatoryIndexFromIdentificator(
          orderOneSignature.identificator as string,
        );
      const hasSignatoryChanged = currentSignatoryIndex !== lastSignatoryIndex;
      if (hasSignatoryChanged) {
        signatureCounter = 0;
        lastSignatoryIndex = currentSignatoryIndex;
      }

      restOfTheSignatures.push({
        ...orderOneSignature,
        order: fileNumber + 1,
        rank: fileNumber + 1,
        identificator: SignatureHelper.buildSignatureIdentificator(
          fileNumber,
          SignatureHelper.getSignatoryIndexFromIdentificator(
            orderOneSignature.identificator as string,
          ),
          signatureCounter++,
        ),
      });
    }
  }

  return [...orderOneSignatures, ...restOfTheSignatures];
}

export const currentTransactionStore = createStore<CurrentTransactionHandler>(
  initialState,
)
  .on<TransactionDetail>(
    setCurrentTransactionDetail,
    (state: CurrentTransactionHandler, payload: TransactionDetail) => {
      return {
        ...state,
        transactionDetail: payload,
      };
    },
  )
  .on<TransactionUpdateField[]>(
    setCurrentTransactionField,
    (state: CurrentTransactionHandler, payload: TransactionUpdateField[]) => {
      return {
        ...state,
        transactionForm: {
          ...state.transactionForm,
          ...payload.reduce(
            (obj, cur) => ({ ...obj, [cur.field]: cur.value }),
            {},
          ),
        },
      };
    },
  )
  .on<RemoveTransactionFilePayload>(
    removeTransactionFile,
    (
      state: CurrentTransactionHandler,
      payload: RemoveTransactionFilePayload,
    ) => {
      return {
        ...state,
        files: state.files.filter((file) => file.key !== payload.key),
      };
    },
  )
  .on<TransactionType>(
    setTransactionType,
    (state: CurrentTransactionHandler, payload: TransactionType) => {
      return {
        ...state,
        transactionType: payload,
      };
    },
  )
  .on<File[]>(
    setTransactionFiles,
    (state: CurrentTransactionHandler, payload: File[]) => {
      return {
        ...state,
        files: FileHelper.removeNameDoublon(payload),
      };
    },
  )
  .on<File[]>(
    setTransactionAttachments,
    (state: CurrentTransactionHandler, payload: File[]) => {
      return {
        ...state,
        attachments: FileHelper.removeNameDoublon(payload),
      };
    },
  )
  .on<RemoveTransactionFilePayload>(
    removeTransactionAttachment,
    (
      state: CurrentTransactionHandler,
      payload: RemoveTransactionFilePayload,
    ) => {
      return {
        ...state,
        attachments: state.attachments.filter(
          (attachment) => attachment.key !== payload.key,
        ),
      };
    },
  )
  .on<Signatory[]>(
    setTransactionSignatories,
    (state: CurrentTransactionHandler, payload: Signatory[]) => {
      return {
        ...state,
        signatories: payload,
      };
    },
  )
  .on<Signature[]>(
    setTransactionSignatures,
    (state: CurrentTransactionHandler, payload: Signature[]) => {
      return {
        ...state,
        signatures: payload,
      };
    },
  )
  .on<Signature>(
    addTransactionSignature,
    (state: CurrentTransactionHandler, payload: Signature) => {
      return {
        ...state,
        signatures: updateSignatures(state.signatures, payload),
      };
    },
  )
  .on<ResetPicture>(
    resetTransactionSignature,
    (state: CurrentTransactionHandler, payload: ResetPicture) => {
      const signatureToResetIndex = state.signatures.findIndex(
        (signature) => signature.identificator === payload.identificator,
      );
      if (signatureToResetIndex !== -1) {
        const updatedSignatures = [...state.signatures];
        updatedSignatures[signatureToResetIndex] = buildEmptySignature({
          email: payload.email,
          rank: payload.rank,
          pattern: payload.pattern,
          signaturePictureRank: payload.signaturePictureRank,
          signaturePriorityRule: payload.signaturePriorityRule,
        });
        return {
          ...state,
          signatures: updatedSignatures,
        };
      } else {
        return {
          ...state,
        };
      }
    },
  )
  .on<ChangePictureName>(
    changeTransactionSignatureName,
    (state: CurrentTransactionHandler, payload: ChangePictureName) => {
      const signaturesToUpdate = [...state.signatures].filter(
        (value) => value.id === payload.id && value.order === payload.order,
      );

      const updatedSignatures = signaturesToUpdate.map((signature) => ({
        ...signature,
        name: payload.name,
      }));

      return {
        ...state,
        signatures: [...state.signatures]
          .filter((value) => value.id !== payload.id)
          .concat(updatedSignatures),
      };
    },
  )
  .on<MoveSignature>(
    moveTransactionSignature,
    (state: CurrentTransactionHandler, payload: MoveSignature) => {
      const signatureToMoveIndex = state.signatures.findIndex((signature) => {
        return signature.identificator === payload.identificator;
      });
      const signatureToMove = state.signatures.at(signatureToMoveIndex);
      const signatoryIndex = state.signatories.findIndex((signatory) => {
        return signatory.email === signatureToMove?.email;
      });

      if (signatureToMoveIndex !== -1) {
        let updatedSignatures = [...state.signatures];
        updatedSignatures[signatureToMoveIndex] = {
          ...updatedSignatures[signatureToMoveIndex],
          ...payload,
        };
        updatedSignatures = SignatureHelper.shouldAddOrDeleteASignature(
          signatureToMove as Signature,
          payload,
          updatedSignatures,
          signatoryIndex,
          state.signatories[signatoryIndex],
        );

        return {
          ...state,
          signatures: updatedSignatures,
        };
      } else {
        return {
          ...state,
        };
      }
    },
  )
  .on<MoveSignature>(
    moveModelOrScellementTransactionSignature,
    (state: CurrentTransactionHandler, payload: MoveSignature) => {
      const signatureToMoveIndex = state.signatures.findIndex((signature) => {
        return signature.identificator === payload.identificator;
      });

      if (signatureToMoveIndex !== -1) {
        const updatedSignatures = [...state.signatures];
        updatedSignatures[signatureToMoveIndex] = {
          ...updatedSignatures[signatureToMoveIndex],
          ...payload,
        };
        return {
          ...state,
          signatures: updatedSignatures,
        };
      } else {
        return {
          ...state,
        };
      }
    },
  )
  .on<DeleteSignature>(
    deleteTransactionSignature,
    (state: CurrentTransactionHandler, payload: DeleteSignature) => {
      // removes all signature of the signatory contained in the payload
      const payloadSignatory =
        SignatureHelper.getSignatoryIndexFromIdentificator(
          payload.identificator ?? "",
        );
      const signatures = [...state.signatures].filter((signature) => {
        const signatureSignatory =
          SignatureHelper.getSignatoryIndexFromIdentificator(
            signature.identificator ?? "",
          );
        return signatureSignatory !== payloadSignatory;
      });

      return {
        ...state,
        signatures,
      };
    },
  )
  .on<PicturePriority>(
    changeTransactionSignaturePicturePriorityForAll,
    (state: CurrentTransactionHandler, payload: PicturePriority) => {
      return {
        ...state,
        signatures: state.signatures.map((signature) => {
          if (signature.email === payload.email) {
            signature.signaturePriorityRule = payload.signaturePriorityRule;
            signature.signaturePictureRank = payload.signaturePictureRank;
            signature.pattern = SignatureHelper.computePattern(
              payload.signaturePriorityRule,
              payload.email ?? undefined,
              signature.pattern,
            );
          }
          return signature;
        }),
      };
    },
  )
  .on<void>(
    changeSignatureFromFirstRank,
    (state: CurrentTransactionHandler) => {
      return {
        ...state,
        signatures: applySignatureFromFirstRank(state.signatures, state.files),
      };
    },
  )
  .on<File>(
    updateFileWithPageNumber,
    (state: CurrentTransactionHandler, payload: File) => {
      const newFiles = state.files.map((file) => {
        if (file.key === payload.key) return payload;
        else return file;
      });
      return {
        ...state,
        files: newFiles,
      };
    },
  )
  .on<void>(resetTransaction, () => {
    return initialState;
  })
  .on<ModelTransaction>(
    setCurrentTransactionFromModel,
    (state: CurrentTransactionHandler, payload: ModelTransaction) => {
      const signatures = TransactionHelper.getSignaturesFromModelTransaction(
        payload.transactionType,
        payload.documents,
        payload.signatures,
        payload.signatories ?? [],
      );
      return {
        ...state,
        attachments: TransactionHelper.getAttachmentsFromModelAttachments(
          payload.attachments ?? [],
        ),
        files: TransactionHelper.getFilesFromModelDocuments(payload.documents),
        signatures,
        signatories: payload.signatories ?? [],
        transactionType: payload.transactionType,
        transactionForm: {
          extraRecipients: payload.extraRecipients,
          invitationMode: payload.invitationMode,
          confidential: payload.isConfidential,
          office: payload.officeId,
          reference: `${payload.reference} ${DateHelper.dayAndMonthFromDate(new Date())}`,
          modelTransactionId: payload.id,
        },
        isSignaturesFromModel: signatures.length > 0,
      };
    },
  )
  .on<SignatoryPattern>(
    setSignatoryPattern,
    (state: CurrentTransactionHandler, payload: SignatoryPattern) => {
      const signatoriesWithNewPattern = state.signatories.map((signatory) => {
        if (signatory.email === payload.email) {
          return {
            ...signatory,
            pattern: payload.pattern,
          };
        } else {
          return signatory;
        }
      });
      return { ...state, signatories: signatoriesWithNewPattern };
    },
  )
  .on<SignatoryLevel>(
    setSignatoryLevel,
    (state: CurrentTransactionHandler, payload: SignatoryLevel) => {
      const signatoriesWithNewLevel = state.signatories.map((signatory) => {
        if (signatory.email === payload.email) {
          return {
            ...signatory,
            level: payload.level,
          };
        } else {
          return signatory;
        }
      });
      return { ...state, signatories: signatoriesWithNewLevel };
    },
  )
  .on<SignatoryWithCertificate>(
    setSignatoryWithCertificate,
    (state: CurrentTransactionHandler, payload: SignatoryWithCertificate) => {
      const newSignatories = state.signatories.map((signatory) => {
        if (signatory.email === payload.email) {
          return {
            ...signatory,
            withCertificate: payload.withCertificate,
          };
        } else {
          return signatory;
        }
      });
      return { ...state, signatories: newSignatories };
    },
  )
  .on<string>(
    setSignatoryPhoneIsUserJSE,
    (state: CurrentTransactionHandler, payload: string) => {
      const newSignatoriesWithPhoneUpdated = state.signatories.map(
        (signatory) => {
          if (signatory.email === payload) {
            return {
              ...signatory,
              phone: "Utilisateur Jesignexpert",
            };
          } else {
            return signatory;
          }
        },
      );
      return { ...state, signatories: newSignatoriesWithPhoneUpdated };
    },
  )
  .on(requestCreateTransaction.doneData, (state, payload) => {
    return {
      ...state,
      transactionDetail: payload?.data,
    };
  })
  .on(requestCreateModelTransaction.doneData, (state, payload) => {
    const transactionDetail: TransactionDetail | undefined = {
      id: payload?.data?.id ?? "",
      type: payload.data?.transactionType,
    };
    return {
      ...state,
      transactionDetail,
    };
  })
  .on(requestUpdateModelTransaction.doneData, (state, payload) => {
    const transactionDetail: TransactionDetail | undefined = {
      id: payload?.data?.id ?? "",
      type: payload.data?.transactionType,
    };
    return {
      ...state,
      transactionDetail,
    };
  })
  .on<boolean>(
    setTransactionIsFromDraft,
    (state: CurrentTransactionHandler, payload: boolean) => {
      return {
        ...state,
        isFromDraft: payload,
      };
    },
  )
  .on<InitialFieldsWithFileName>(
    applyInitialFieldsToFile,
    (state: CurrentTransactionHandler, payload: InitialFieldsWithFileName) => {
      const filesWithInitialFieldsAdded = state.files.map((file) => {
        if (file.name && payload.fileName && file.name === payload.fileName) {
          return {
            ...file,
            initialFields: { y: payload.y, alignment: payload.alignment },
          };
        }
        return file;
      });
      return {
        ...state,
        files: filesWithInitialFieldsAdded,
      };
    },
  )
  .on<string | undefined>(
    removeInitialFieldsFromFileName,
    (state: CurrentTransactionHandler, payload: string | undefined) => {
      const filesWithInitialFieldsRemoved = state.files.map((file) => {
        if (file.name && payload && file.name === payload) {
          return {
            ...file,
            initialFields: undefined,
          };
        }
        return file;
      });
      return {
        ...state,
        files: filesWithInitialFieldsRemoved,
      };
    },
  )
  .on<void>(
    applyFirstInitialFieldsToOthers,
    (state: CurrentTransactionHandler) => {
      const files = [...state.files];
      if (files.length < 2) {
        return state;
      }
      const firstFileInitialFields = files[0].initialFields;
      return {
        ...state,
        files: files.map((file) => ({
          ...file,
          initialFields: firstFileInitialFields,
        })),
      };
    },
  )
  .on<SignatureRequirementMode>(
    setSignatureRequirementMode,
    (state, payload) => {
      return {
        ...state,
        signatureRequirementMode: payload,
      };
    },
  );

sample({
  clock: setCurrentTransactionField,
  source: currentTransactionStore,
  fn: (_, eventPayload: TransactionUpdateField[]) => {
    return eventPayload;
  },
  target: afterTransactionFieldUpdated,
});
